import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { useBalance } from '../hooks/useBalance';
import { useAuth } from '../context/useAuth.jsx';
import { Button } from './ui';
import axios from 'axios';

function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { balance, refetch } = useBalance();
  const { refreshUser, refreshSession } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const confirmAndRefresh = async () => {
      if (sessionId) {
        try {
          await axios.post(`/api/v1/topup/confirm-checkout?session_id=${sessionId}`);
        } catch (e) {
          // Already processed or webhook handled it — not an error
          console.debug('confirm-checkout:', e?.response?.data?.detail || e.message);
        }
      }
      // Refresh balance + JWT tier in parallel — the tier claim may have
      // changed if this checkout was a plan upgrade (not just a top-up). The
      // tier lives in the access token, so mint a new one (force) as well as
      // reloading the profile.
      await Promise.all([refetch(), refreshSession({ force: true }), refreshUser()]);
      setLoading(false);
    };

    confirmAndRefresh();
  }, [sessionId, refetch, refreshUser, refreshSession]);

  return (
    <div className="min-h-dvh bg-app-bg py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-app-border shadow-sm p-10 text-center">
          {loading ? (
            <>
              <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" strokeWidth={1.75} />
              <h1 className="text-2xl font-semibold text-app-text mb-2">
                Processing payment
              </h1>
              <p className="text-app-text-muted">
                Updating your balance — this only takes a moment.
              </p>
            </>
          ) : (
            <>
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" strokeWidth={1.75} />
              <h1 className="text-2xl font-semibold text-app-text mb-2">
                Payment received
              </h1>
              <p className="text-app-text-muted mb-8">
                Your account has been topped up.
              </p>

              {balance && (
                <div className="bg-app-panel-muted border border-app-border rounded-lg p-4 mb-8">
                  <div className="text-xs uppercase tracking-wide text-app-text-subtle mb-1">New balance</div>
                  <div className="text-2xl font-semibold text-app-text tabular-nums">
                    £{Number(balance.available).toFixed(2)}
                  </div>
                </div>
              )}

              <Button variant="primary" size="md" onClick={() => navigate('/runs')}>
                Continue to workflows
                <ArrowRight className="w-4 h-4" />
              </Button>

              {sessionId && (
                <p className="text-xs text-app-text-subtle mt-6 font-mono">
                  Session ID: {sessionId}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default BillingSuccess;
